import { motion } from "framer-motion";
import { Link } from "wouter";

const headlineLines = [
  [{ text: "Proud", accent: false }, { text: "Welsh", accent: false }],
  [{ text: "Content", accent: true }, { text: "Producers", accent: true }],
];

const wordReveal = {
  hidden: { y: "110%" },
  visible: (i: number) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.08, ease: [0.16, 1, 0.3, 1] as const },
  }),
};

export function Hero() {
  const baseUrl = import.meta.env.BASE_URL;
  let wordIndex = 0;

  return (
    <section
      id="home"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-32"
    >
      {/* Background image with slow Ken Burns drift */}
      <div className="absolute inset-0 z-0">
        <motion.img
          src={`${baseUrl}brand/mark.png`}
          alt="Black Swan Mark"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.35 }}
          transition={{ scale: { duration: 18, ease: "easeOut" }, opacity: { duration: 2 } }}
          className="w-full h-full object-cover object-center"
        />
        {/* Vignette + tonal grading */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#000000_95%)]" />
      </div>

      {/* Static purple glow blobs. These were previously animated, but
          continuously re-rasterizing a 140-150px blur is extremely expensive on
          mobile/iOS GPUs and caused jank, so they are now static. */}
      <div
        aria-hidden
        className="absolute -top-32 -left-20 z-0 h-[36rem] w-[36rem] rounded-full bg-primary/25 blur-[140px] opacity-60"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-24 z-0 h-[34rem] w-[34rem] rounded-full bg-purple-700/20 blur-[150px] opacity-50"
      />

      {/* Film grain */}
      <div className="grain-overlay z-[1] mix-blend-overlay" />

      <div className="container relative z-10 mx-auto px-4 md:px-6 flex flex-col items-center text-center">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
          className="mb-8"
        >
          <img
            src={`${baseUrl}brand/logo-primary.png`}
            alt="Black Swan Productions"
            className="w-28 md:w-40 mx-auto object-contain mix-blend-screen"
          />
        </motion.div>

        {/* Headline with masked word reveal */}
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-white mb-6 uppercase text-balance leading-[0.95]">
          {headlineLines.map((line, li) => (
            <span key={li} className="block">
              {line.map((word) => {
                const i = wordIndex++;
                return (
                  <span
                    key={i}
                    className="inline-block overflow-hidden align-bottom pb-[0.05em] mr-[0.28em] last:mr-0"
                  >
                    <motion.span
                      custom={i}
                      variants={wordReveal}
                      initial="hidden"
                      animate="visible"
                      className={
                        word.accent
                          ? "inline-block bg-gradient-to-r from-primary via-purple-300 to-primary bg-clip-text text-transparent animate-text-shimmer"
                          : "inline-block"
                      }
                    >
                      {word.text}
                    </motion.span>
                  </span>
                );
              })}
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.7 }}
          className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-12"
        >
          Award-winning multifaceted content producers spanning reputable corporate work to daring scripted productions, based in Great Point Studios, Cardiff. We're not just content producers, but a creative entrepreneurial group enhancing the Welsh ecosystem since 2021.
        </motion.p>

        {/* Dual CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.85 }}
          className="flex flex-col sm:flex-row items-center gap-4"
        >
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary-strong text-white font-semibold text-lg uppercase tracking-wider rounded-full hover:bg-white hover:text-black transition-colors duration-300"
          >
            Start a project
          </Link>
          <Link
            href="/about"
            className="inline-flex items-center justify-center px-8 py-4 border border-white/20 text-white font-semibold text-lg uppercase tracking-wider rounded-full hover:bg-white/10 hover:border-white/40 transition-colors duration-300"
          >
            Learn about us
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
